import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, Plus, Phone, Mail, MessageSquare, Calendar, 
  ChevronRight, Search, ArrowRight, Check, Clock, AlertTriangle,
  UserPlus, PhoneCall, TestTube, Handshake, Trophy, XCircle, Loader2,
  MoreVertical, Trash2, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

import api from '../utils/api';

const STAGES = [
  { id: 'new', label: 'New', icon: UserPlus, color: 'bg-blue-500', light: 'bg-blue-50 text-blue-700 border-blue-200' },
  { id: 'contacted', label: 'Contacted', icon: PhoneCall, color: 'bg-indigo-500', light: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  { id: 'qualified', label: 'Qualified', icon: TestTube, color: 'bg-violet-500', light: 'bg-violet-50 text-violet-700 border-violet-200' },
  { id: 'negotiation', label: 'Negotiation', icon: Handshake, color: 'bg-amber-500', light: 'bg-amber-50 text-amber-700 border-amber-200' },
  { id: 'won', label: 'Won', icon: Trophy, color: 'bg-emerald-500', light: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { id: 'lost', label: 'Lost', icon: XCircle, color: 'bg-red-400', light: 'bg-red-50 text-red-600 border-red-200' },
];

const SOURCES = [
  { id: 'manual', label: 'Manual Entry' },
  { id: 'lead', label: 'Platform Lead' },
  { id: 'walk-in', label: 'Walk-In' },
  { id: 'referral', label: 'Referral' },
  { id: 'website', label: 'Website' },
];

const INTERACTION_TYPES = [
  { id: 'call', label: 'Phone Call', icon: Phone },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'text', label: 'Text Message', icon: MessageSquare },
  { id: 'meeting', label: 'Meeting', icon: Users },
  { id: 'test_drive', label: 'Test Drive', icon: TestTube },
  { id: 'follow_up', label: 'Follow Up', icon: Calendar },
];

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function DealerCRM() {
  const [contacts, setContacts] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [view, setView] = useState('pipeline'); // pipeline, list, contact
  const [selectedContact, setSelectedContact] = useState(null);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showAddInteraction, setShowAddInteraction] = useState(false);
  const [showAddFollowUp, setShowAddFollowUp] = useState(false);
  const [saving, setSaving] = useState(false);

  const [contactForm, setContactForm] = useState({
    name: '', email: '', phone: '', source: 'manual', stage: 'new', interest: '', notes: ''
  });
  const [interactionForm, setInteractionForm] = useState({
    interaction_type: 'call', summary: '', outcome: ''
  });
  const [followUpForm, setFollowUpForm] = useState({
    due_date: '', task: '', priority: 'medium'
  });

  const fetchData = useCallback(async () => {
    try {
      const [contactsRes, pipelineRes, followUpsRes] = await Promise.all([
        api.get('/api/crm/contacts'),
        api.get('/api/crm/pipeline'),
        api.get('/api/crm/follow-ups/upcoming')
      ]);
      setContacts(contactsRes.data);
      setPipeline(pipelineRes.data);
      setFollowUps(followUpsRes.data);
    } catch (err) {
      console.error('Failed to fetch CRM data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name.trim()) return;
    setSaving(true);
    try {
      await api.post('/api/crm/contacts', contactForm);
      toast.success('Contact added to pipeline!');
      setShowAddContact(false);
      setContactForm({ name: '', email: '', phone: '', source: 'manual', stage: 'new', interest: '', notes: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create contact');
    } finally {
      setSaving(false);
    }
  };

  const handleStageChange = async (contactId, newStage) => {
    try {
      await api.post(`/api/crm/contacts/${contactId}/stage`, { stage: newStage });
      setContacts(prev => prev.map(c => c.id === contactId ? { ...c, stage: newStage } : c));
      setPipeline(prev => {
        if (!prev) return prev;
        const contact = contacts.find(c => c.id === contactId);
        if (!contact) return prev;
        const updated = { ...prev, stages: { ...prev.stages } };
        updated.stages[contact.stage] = Math.max(0, (updated.stages[contact.stage] || 0) - 1);
        updated.stages[newStage] = (updated.stages[newStage] || 0) + 1;
        return updated;
      });
      toast.success(`Moved to ${STAGES.find(s => s.id === newStage)?.label}`);
    } catch (err) {
      toast.error('Failed to update stage');
    }
  };

  const handleViewContact = async (contactId) => {
    try {
      const { data } = await api.get(`/api/crm/contacts/${contactId}`);
      setSelectedContact(data);
      setView('contact');
    } catch (err) {
      toast.error('Failed to load contact');
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/api/crm/contacts/${contactId}`);
      toast.success('Contact deleted');
      setSelectedContact(null);
      setView('pipeline');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete contact');
    }
  };

  const handleLogInteraction = async (e) => {
    e.preventDefault();
    if (!interactionForm.summary.trim()) return;
    setSaving(true);
    try {
      await api.post('/api/crm/interactions', {
        contact_id: selectedContact.id,
        ...interactionForm
      });
      toast.success('Interaction logged!');
      setShowAddInteraction(false);
      setInteractionForm({ interaction_type: 'call', summary: '', outcome: '' });
      handleViewContact(selectedContact.id);
      fetchData();
    } catch (err) {
      toast.error('Failed to log interaction');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateFollowUp = async (e) => {
    e.preventDefault();
    if (!followUpForm.task.trim() || !followUpForm.due_date) return;
    setSaving(true);
    try {
      await api.post('/api/crm/follow-ups', {
        contact_id: selectedContact.id,
        ...followUpForm
      });
      toast.success('Follow-up scheduled!');
      setShowAddFollowUp(false);
      setFollowUpForm({ due_date: '', task: '', priority: 'medium' });
      handleViewContact(selectedContact.id);
      fetchData();
    } catch (err) {
      toast.error('Failed to schedule follow-up');
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteFollowUp = async (followUpId) => {
    try {
      await api.post(`/api/crm/follow-ups/${followUpId}/complete?contact_id=${selectedContact.id}`);
      toast.success('Follow-up completed!');
      handleViewContact(selectedContact.id);
      fetchData();
    } catch (err) {
      toast.error('Failed to complete follow-up');
    }
  };

  const filteredContacts = contacts.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.phone?.includes(q);
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-gray-100 rounded-2xl animate-pulse" />)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[...Array(6)].map((_, i) => <div key={i} className="h-48 bg-gray-100 rounded-2xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  // ============ CONTACT DETAIL VIEW ============
  if (view === 'contact' && selectedContact) {
    const stageInfo = STAGES.find(s => s.id === selectedContact.stage);
    return (
      <div data-testid="crm-contact-detail">
        {/* Back button + header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="outline" className="rounded-xl" onClick={() => { setView('pipeline'); setSelectedContact(null); }} data-testid="crm-back-btn">
            <ChevronRight className="w-4 h-4 rotate-180" /> Back
          </Button>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{selectedContact.name}</h2>
            <div className="flex items-center gap-3 mt-1">
              {stageInfo && (
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${stageInfo.light}`}>
                  {stageInfo.label}
                </span>
              )}
              {selectedContact.source && <span className="text-xs text-gray-500">via {selectedContact.source}</span>}
            </div>
          </div>
          <Button variant="outline" className="rounded-xl text-red-500 hover:bg-red-50 hover:text-red-600" onClick={() => handleDeleteContact(selectedContact.id)} data-testid="crm-delete-contact-btn">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Info + Stage */}
          <div className="space-y-4">
            {/* Contact Info Card */}
            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3">Contact Info</h3>
              <div className="space-y-2.5 text-sm">
                {selectedContact.email && <div className="flex items-center gap-2 text-gray-600"><Mail className="w-4 h-4" />{selectedContact.email}</div>}
                {selectedContact.phone && <div className="flex items-center gap-2 text-gray-600"><Phone className="w-4 h-4" />{selectedContact.phone}</div>}
                {selectedContact.interest && <div className="text-gray-600"><span className="font-medium">Interest:</span> {selectedContact.interest}</div>}
                {selectedContact.notes && <div className="text-gray-500 bg-gray-50 p-3 rounded-lg text-xs">{selectedContact.notes}</div>}
              </div>
            </div>

            {/* Pipeline Stage */}
            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3">Move to Stage</h3>
              <div className="grid grid-cols-2 gap-2">
                {STAGES.map(s => (
                  <button
                    key={s.id}
                    onClick={() => handleStageChange(selectedContact.id, s.id)}
                    className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all ${selectedContact.stage === s.id ? s.light + ' ring-2 ring-offset-1' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
                    data-testid={`crm-stage-btn-${s.id}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Follow-ups */}
            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">Follow-ups</h3>
                <Button size="sm" variant="outline" className="rounded-lg h-8 text-xs" onClick={() => setShowAddFollowUp(true)} data-testid="crm-add-followup-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                {(selectedContact.follow_ups || []).length === 0 && <p className="text-xs text-gray-400 text-center py-3">No follow-ups scheduled</p>}
                {(selectedContact.follow_ups || []).map(fu => (
                  <div key={fu.id} className={`p-3 rounded-xl text-xs border ${fu.completed ? 'bg-gray-50 border-gray-100 opacity-60' : 'bg-amber-50 border-amber-100'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className={`font-medium ${fu.completed ? 'line-through text-gray-400' : 'text-gray-800'}`}>{fu.task}</p>
                        <p className="text-gray-500 mt-0.5">Due: {new Date(fu.due_date).toLocaleDateString()}</p>
                      </div>
                      {!fu.completed && (
                        <button onClick={() => handleCompleteFollowUp(fu.id)} className="text-green-600 hover:text-green-800" data-testid={`crm-complete-followup-${fu.id}`}>
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Interactions Timeline */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Activity Timeline</h3>
                <Button size="sm" className="rounded-xl bg-gradient-to-r from-indigo-500 to-blue-500 text-white" onClick={() => setShowAddInteraction(true)} data-testid="crm-add-interaction-btn">
                  <Plus className="w-4 h-4 mr-1" /> Log Activity
                </Button>
              </div>
              <div className="space-y-4">
                {(selectedContact.interactions || []).length === 0 && (
                  <div className="text-center py-8 text-gray-400">
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No interactions yet. Log your first activity!</p>
                  </div>
                )}
                {(selectedContact.interactions || []).map((interaction) => {
                  const typeInfo = INTERACTION_TYPES.find(t => t.id === interaction.interaction_type);
                  const Icon = typeInfo?.icon || MessageSquare;
                  return (
                    <div key={interaction.id} className="flex gap-3" data-testid={`crm-interaction-${interaction.id}`}>
                      <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4 text-indigo-600" />
                      </div>
                      <div className="flex-1 pb-4 border-b border-gray-50">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-sm text-gray-900">{typeInfo?.label || interaction.interaction_type}</p>
                          <span className="text-xs text-gray-400">{timeAgo(interaction.created_at)}</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{interaction.summary}</p>
                        {interaction.outcome && <p className="text-xs text-gray-500 mt-1 bg-gray-50 px-3 py-1.5 rounded-lg">Outcome: {interaction.outcome}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Log Interaction Modal */}
        <Dialog open={showAddInteraction} onOpenChange={setShowAddInteraction}>
          <DialogContent className="max-w-md rounded-2xl">
            <DialogHeader><DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Log Activity</DialogTitle></DialogHeader>
            <form onSubmit={handleLogInteraction} className="space-y-4 pt-2">
              <div>
                <Label className="text-gray-700 font-medium">Type</Label>
                <Select value={interactionForm.interaction_type} onValueChange={v => setInteractionForm(p => ({ ...p, interaction_type: v }))}>
                  <SelectTrigger data-testid="crm-interaction-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INTERACTION_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Summary *</Label>
                <Textarea placeholder="What happened?" value={interactionForm.summary} onChange={e => setInteractionForm(p => ({ ...p, summary: e.target.value }))} data-testid="crm-interaction-summary" />
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Outcome</Label>
                <Input placeholder="What was the result?" value={interactionForm.outcome} onChange={e => setInteractionForm(p => ({ ...p, outcome: e.target.value }))} data-testid="crm-interaction-outcome" />
              </div>
              <Button type="submit" disabled={saving} className="w-full rounded-xl bg-gradient-to-r from-indigo-500 to-blue-500 text-white" data-testid="crm-save-interaction-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save Activity
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Follow-up Modal */}
        <Dialog open={showAddFollowUp} onOpenChange={setShowAddFollowUp}>
          <DialogContent className="max-w-md rounded-2xl">
            <DialogHeader><DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Schedule Follow-up</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateFollowUp} className="space-y-4 pt-2">
              <div>
                <Label className="text-gray-700 font-medium">Task *</Label>
                <Input placeholder="e.g., Call back about financing" value={followUpForm.task} onChange={e => setFollowUpForm(p => ({ ...p, task: e.target.value }))} data-testid="crm-followup-task" />
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Due Date *</Label>
                <Input type="date" value={followUpForm.due_date} onChange={e => setFollowUpForm(p => ({ ...p, due_date: e.target.value }))} data-testid="crm-followup-date" />
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Priority</Label>
                <Select value={followUpForm.priority} onValueChange={v => setFollowUpForm(p => ({ ...p, priority: v }))}>
                  <SelectTrigger data-testid="crm-followup-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={saving} className="w-full rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white" data-testid="crm-save-followup-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />} Schedule
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ============ PIPELINE / LIST VIEW ============
  return (
    <div data-testid="crm-pipeline-view">
      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Total Contacts</p>
          <p className="text-3xl font-bold text-gray-900 mt-1" data-testid="crm-total-contacts">{pipeline?.total_contacts || 0}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Close Rate</p>
          <p className="text-3xl font-bold text-emerald-600 mt-1" data-testid="crm-close-rate">{pipeline?.close_rate || 0}%</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Deals Won</p>
          <p className="text-3xl font-bold text-green-600 mt-1" data-testid="crm-deals-won">{pipeline?.won || 0}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Follow-ups Due</p>
          <p className="text-3xl font-bold text-amber-600 mt-1" data-testid="crm-followups-due">{pipeline?.upcoming_follow_ups || 0}</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex-1 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-10 h-10 rounded-xl border-gray-200"
            data-testid="crm-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setView('pipeline')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${view === 'pipeline' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}
              data-testid="crm-view-pipeline"
            >Pipeline</button>
            <button
              onClick={() => setView('list')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${view === 'list' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}
              data-testid="crm-view-list"
            >List</button>
          </div>
          <Button className="rounded-xl bg-gradient-to-r from-indigo-500 to-blue-500 text-white shadow-lg shadow-indigo-200" onClick={() => setShowAddContact(true)} data-testid="crm-add-contact-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Contact
          </Button>
        </div>
      </div>

      {/* Upcoming Follow-ups Banner */}
      {followUps.length > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-100 rounded-2xl p-4" data-testid="crm-followups-banner">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">Upcoming Follow-ups ({followUps.length})</span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1">
            {followUps.slice(0, 5).map(fu => (
              <div key={fu.id} className="shrink-0 bg-white rounded-xl p-3 border border-amber-100 min-w-[200px]" onClick={() => handleViewContact(fu.contact_id)}>
                <p className="text-sm font-medium text-gray-900 cursor-pointer hover:text-indigo-600">{fu.contact_name}</p>
                <p className="text-xs text-gray-600 mt-0.5 truncate">{fu.task}</p>
                <p className="text-xs text-amber-600 mt-1">Due: {new Date(fu.due_date).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pipeline View */}
      {view === 'pipeline' && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="crm-pipeline-columns">
          {STAGES.map(stage => {
            const stageContacts = filteredContacts.filter(c => c.stage === stage.id);
            return (
              <div key={stage.id} className="min-h-[300px]">
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-6 h-6 rounded-lg ${stage.color} flex items-center justify-center`}>
                    <stage.icon className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-700">{stage.label}</span>
                  <span className="ml-auto text-xs font-medium bg-gray-100 px-2 py-0.5 rounded-full text-gray-500">{stageContacts.length}</span>
                </div>
                <div className="space-y-2">
                  {stageContacts.map(contact => (
                    <div
                      key={contact.id}
                      className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm hover:shadow-md transition-all cursor-pointer group"
                      onClick={() => handleViewContact(contact.id)}
                      data-testid={`crm-contact-card-${contact.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <p className="text-sm font-medium text-gray-900 group-hover:text-indigo-600 transition-colors">{contact.name}</p>
                        <Eye className="w-3.5 h-3.5 text-gray-300 group-hover:text-indigo-400" />
                      </div>
                      {contact.interest && <p className="text-xs text-gray-500 mt-1 truncate">{contact.interest}</p>}
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-400">{timeAgo(contact.updated_at)}</span>
                        {contact.interactions_count > 0 && (
                          <span className="text-xs text-indigo-500 font-medium">{contact.interactions_count} activities</span>
                        )}
                      </div>
                    </div>
                  ))}
                  {stageContacts.length === 0 && (
                    <div className="text-center py-6 text-gray-300">
                      <p className="text-xs">No contacts</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List View */}
      {view === 'list' && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="crm-list-view">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Name</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Contact</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Interest</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Stage</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Activity</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filteredContacts.map(contact => {
                const stageInfo = STAGES.find(s => s.id === contact.stage);
                return (
                  <tr key={contact.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => handleViewContact(contact.id)} data-testid={`crm-list-row-${contact.id}`}>
                    <td className="px-5 py-3">
                      <p className="font-medium text-sm text-gray-900">{contact.name}</p>
                      <p className="text-xs text-gray-400">{contact.source}</p>
                    </td>
                    <td className="px-5 py-3 hidden md:table-cell">
                      <p className="text-sm text-gray-600">{contact.email || '-'}</p>
                      <p className="text-xs text-gray-400">{contact.phone || ''}</p>
                    </td>
                    <td className="px-5 py-3 hidden lg:table-cell text-sm text-gray-600 max-w-[200px] truncate">{contact.interest || '-'}</td>
                    <td className="px-5 py-3">
                      {stageInfo && <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${stageInfo.light}`}>{stageInfo.label}</span>}
                    </td>
                    <td className="px-5 py-3 hidden md:table-cell text-xs text-gray-500">{contact.interactions_count} interactions</td>
                    <td className="px-5 py-3"><ArrowRight className="w-4 h-4 text-gray-300" /></td>
                  </tr>
                );
              })}
              {filteredContacts.length === 0 && (
                <tr><td colSpan={6} className="text-center py-12 text-gray-400 text-sm">No contacts found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Contact Modal */}
      <Dialog open={showAddContact} onOpenChange={setShowAddContact}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Add New Contact</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateContact} className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-gray-700 font-medium">Name *</Label>
                <Input placeholder="Full name" value={contactForm.name} onChange={e => setContactForm(p => ({ ...p, name: e.target.value }))} data-testid="crm-contact-name" />
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Email</Label>
                <Input type="email" placeholder="email@example.com" value={contactForm.email} onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))} data-testid="crm-contact-email" />
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Phone</Label>
                <Input placeholder="555-1234" value={contactForm.phone} onChange={e => setContactForm(p => ({ ...p, phone: e.target.value }))} data-testid="crm-contact-phone" />
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Source</Label>
                <Select value={contactForm.source} onValueChange={v => setContactForm(p => ({ ...p, source: v }))}>
                  <SelectTrigger data-testid="crm-contact-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SOURCES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Stage</Label>
                <Select value={contactForm.stage} onValueChange={v => setContactForm(p => ({ ...p, stage: v }))}>
                  <SelectTrigger data-testid="crm-contact-stage-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STAGES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-gray-700 font-medium">Vehicle Interest</Label>
                <Input placeholder="e.g., 2020 Honda Civic" value={contactForm.interest} onChange={e => setContactForm(p => ({ ...p, interest: e.target.value }))} data-testid="crm-contact-interest" />
              </div>
              <div className="col-span-2">
                <Label className="text-gray-700 font-medium">Notes</Label>
                <Textarea placeholder="Additional notes..." value={contactForm.notes} onChange={e => setContactForm(p => ({ ...p, notes: e.target.value }))} data-testid="crm-contact-notes" />
              </div>
            </div>
            <Button type="submit" disabled={saving} className="w-full rounded-xl h-11 bg-gradient-to-r from-indigo-500 to-blue-500 text-white" data-testid="crm-save-contact-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />} Add to Pipeline
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
